import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MapPin,
  TrendingUp,
  TrendingDown,
  ClipboardList,
  AlertTriangle,
  Truck,
  RefreshCw,
  ChevronRight,
  Zap,
  Info,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  PageHeader,
  LoadingState,
  DataTable,
  Column,
  MetricCard,
  KPIGrid,
} from '@/components/common';
import { siteOptimizationApi, ReplenishmentRecommendation } from '@/api/services';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';

const urgencyColors = {
  CRITICAL: 'bg-danger-50 text-danger-500 border-danger-200',
  HIGH: 'bg-warning-50 text-warning-600 border-warning-200',
  MEDIUM: 'bg-primary-50 text-primary-500 border-primary-200',
  LOW: 'bg-muted text-muted-foreground border-border',
};

// Stock status colors (currently unused)
// const stockStatusColors = {
//   CRITICAL: 'bg-danger-50 text-danger-500 border-danger-200',
//   LOW: 'bg-warning-50 text-warning-600 border-warning-200',
//   OPTIMAL: 'bg-success-50 text-success-600 border-success-200',
//   EXCESS: 'bg-primary-50 text-primary-500 border-primary-200',
// };

export default function SiteOptimization() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [urgencyFilter, setUrgencyFilter] = useState<string>('all');

  // const [summary, setSummary] = useState<SiteOptimizationSummary | null>(null);
  const [recommendations, setRecommendations] = useState<ReplenishmentRecommendation[]>([]);
  const [filteredRecommendations, setFilteredRecommendations] = useState<
    ReplenishmentRecommendation[]
  >([]);

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const recommendationsData = await siteOptimizationApi.getReplenishmentRecommendations();
      setRecommendations(recommendationsData);
    } catch (error) {
      console.error('Failed to load site optimization data:', error);
      toast.error('Failed to load site optimization data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Apply filter
  const applyFilters = useCallback(() => {
    let data = recommendations;

    // Apply urgency filter
    if (urgencyFilter !== 'all') {
      data = data.filter((r) => r.urgency === urgencyFilter);
    }

    // Apply tab filter
    if (activeTab === 'critical') {
      data = data.filter((r) => r.urgency === 'CRITICAL');
    } else if (activeTab === 'high') {
      data = data.filter((r) => r.urgency === 'HIGH');
    }

    setFilteredRecommendations(data);
  }, [activeTab, recommendations, urgencyFilter]);

  useEffect(() => {
    applyFilters();
  }, [activeTab, urgencyFilter, recommendations, applyFilters]);

  if (loading) {
    return <LoadingState />;
  }

  const criticalCount = recommendations.filter((r) => r.urgency === 'CRITICAL').length;
  const highCount = recommendations.filter((r) => r.urgency === 'HIGH').length;
  const sitesAffected = new Set(recommendations.map((r) => r.destinationLocation.id)).size;
  const totalShortfall = recommendations.reduce((sum, r) => sum + (r.shortfall || 0), 0);

  // Urgency cuts across all three tabs, so it lives in each table's control
  // row rather than floating in the card header above them — that row is
  // rendered either way to carry Export and View.
  const urgencySelect = (
    <Select value={urgencyFilter} onValueChange={setUrgencyFilter}>
      <SelectTrigger className="w-48">
        <SelectValue placeholder="Filter by urgency" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All Urgency Levels</SelectItem>
        <SelectItem value="CRITICAL">Critical Only</SelectItem>
        <SelectItem value="HIGH">High Only</SelectItem>
        <SelectItem value="MEDIUM">Medium Only</SelectItem>
        <SelectItem value="LOW">Low Only</SelectItem>
      </SelectContent>
    </Select>
  );

  const columns: Column<ReplenishmentRecommendation>[] = [
    {
      key: 'urgency',
      header: 'Urgency',
      sortable: true,
      render: (rec) => (
        <Badge variant="outline" className={cn('font-semibold', urgencyColors[rec.urgency])}>
          {rec.urgency}
        </Badge>
      ),
    },
    {
      key: 'item',
      linkTo: (rec) => `/inventory/${rec.itemId}`,
      header: 'Item',
      render: (rec) => (
        <div>
          <div className="font-medium">{rec.itemName}</div>
          <div className="text-sm text-muted-foreground font-mono">{rec.itemSku}</div>
        </div>
      ),
    },
    {
      key: 'location',
      header: 'Destination Site',
      render: (rec) => (
        <div>
          <div className="font-medium flex items-center gap-1">{rec.destinationLocation.name}</div>
          <div className="text-sm text-muted-foreground font-mono">
            {rec.destinationLocation.code}
          </div>
        </div>
      ),
    },
    {
      key: 'stock',
      header: 'Stock Status',
      render: (rec) => (
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium">Current: {rec.currentStock}</span>
            {rec.currentStock === 0 && (
              <Badge
                variant="outline"
                className="bg-status-destructive-subtle text-status-destructive-foreground"
              >
                Stockout
              </Badge>
            )}
          </div>
          <div className="text-sm text-muted-foreground">
            Required: {rec.requiredMinStock} (Shortfall: {rec.shortfall})
          </div>
        </div>
      ),
    },
    {
      key: 'recommendation',
      header: 'Replenishment',
      render: (rec) => (
        <div>
          <div className="font-bold text-lg">{rec.recommendedQuantity} units</div>
          {rec.suggestedSourceLocation && (
            <div className="text-xs text-muted-foreground">
              From: {rec.suggestedSourceLocation.name}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'cost',
      header: 'Material Value & Lead Time',
      render: (rec) => (
        <div className="text-sm">
          {/* The value of the material being moved, not the cost of moving it
              — that is priced in the ERP. An item with no unit cost on file
              says so rather than showing a figure. */}
          {rec.estimatedMaterialCost != null ? (
            <div className="font-medium">${rec.estimatedMaterialCost.toLocaleString()}</div>
          ) : (
            <div className="text-muted-foreground">No unit cost on file</div>
          )}
          <div className="text-muted-foreground">{rec.estimatedLeadTimeDays} days</div>
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-32',
      render: (rec) => (
        <div className="flex gap-1">
          <Button variant="outline" size="sm" onClick={() => handleCreateTransfer(rec)}>
            Transfer
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate(`/inventory/${rec.itemId}`)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  const handleCreateTransfer = (rec: ReplenishmentRecommendation) => {
    // TODO: Implement transfer creation
    toast.info(`Creating transfer for ${rec.itemName} to ${rec.destinationLocation.name}`);
  };

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Inventory', href: '/inventory' },
            { label: 'Site Optimization' },
          ]}
          title="Site Stock Optimization"
          description="Recommended stock transfers to field sites, ranked by how far each site is below its minimum."
          actions={
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          }
        />
      </motion.div>

      {/* Every other page in the module leads with its numbers; this one opened
          straight into an explanatory banner and a table. */}
      <motion.div variants={fadeUp}>
        <KPIGrid columns={4}>
          <MetricCard
            label="Recommendations"
            value={recommendations.length}
            sentiment="info"
            icon={<ClipboardList className="h-5 w-5" />}
          />
          <MetricCard
            label="Critical"
            value={criticalCount}
            sentiment="danger"
            icon={<AlertTriangle className="h-5 w-5" />}
            subtitle="stockout risk"
          />
          <MetricCard
            label="Sites Affected"
            value={sitesAffected}
            sentiment="warning"
            icon={<MapPin className="h-5 w-5" />}
          />
          <MetricCard
            label="Total Shortfall"
            value={totalShortfall}
            sentiment="info"
            icon={<TrendingDown className="h-5 w-5" />}
            subtitle="units below minimum"
          />
        </KPIGrid>
      </motion.div>

      {/* Critical Alerts */}
      {criticalCount > 0 && (
        <motion.div variants={fadeUp} className="mt-6">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <div className="flex items-center justify-between">
                <div>
                  <strong>{criticalCount} critical replenishment needs</strong> require immediate
                  attention to prevent stockouts
                </div>
                <Button variant="outline" size="sm" onClick={() => setActiveTab('critical')}>
                  View All
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </motion.div>
      )}

      {/* Top Critical Replenishments */}
      {criticalCount > 0 && (
        <motion.div variants={fadeUp} className="mt-6">
          <Card className="border-l-4 border-l-danger-500 hover:shadow-raised-hover transition-all duration-200">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-foreground tracking-tight">
                <Zap className="h-5 w-5 text-danger-500" />
                Urgent Replenishment Needs
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Critical shortages requiring immediate action
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recommendations
                  .filter((r) => r.urgency === 'CRITICAL')
                  .slice(0, 5)
                  .map((rec) => (
                    <div
                      key={`${rec.itemId}-${rec.destinationLocation.id}`}
                      className="flex items-center justify-between p-3 rounded-lg border border-danger-200 bg-danger-50 hover:shadow-raised-hover cursor-pointer transition-all duration-150"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-foreground">{rec.itemName}</div>
                        <div className="text-xs leading-4 text-muted-foreground">
                          <MapPin className="h-3 w-3 inline mr-1" />
                          {rec.destinationLocation.name}
                        </div>
                      </div>
                      <div className="flex-shrink-0 text-center mx-4">
                        <div className="font-bold text-danger-500 text-lg">
                          {rec.recommendedQuantity}
                        </div>
                        <div className="text-xs text-muted-foreground">units needed</div>
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <Badge variant="outline" className={cn('mb-1', urgencyColors[rec.urgency])}>
                          {rec.urgency}
                        </Badge>
                        <div className="text-xs text-muted-foreground">
                          {rec.estimatedMaterialCost != null
                            ? `$${rec.estimatedMaterialCost.toLocaleString()}`
                            : 'Not costed'}
                        </div>
                      </div>
                      <Button
                        variant="default"
                        size="sm"
                        className="ml-3"
                        onClick={() => handleCreateTransfer(rec)}
                      >
                        <Truck className="h-3 w-3 mr-1" />
                        Transfer
                      </Button>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Replenishment Opportunities */}
      {recommendations.some((r) => r.suggestedSourceLocation) && (
        <motion.div variants={fadeUp} className="mt-6">
          <Card className="border-border hover:shadow-raised-hover transition-all duration-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-foreground tracking-tight">
                <TrendingUp className="h-5 w-5 text-success-600" />
                Smart Replenishment Suggestions
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Transfer from nearby sites with excess stock
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {recommendations
                  .filter(
                    (r) =>
                      r.suggestedSourceLocation &&
                      (r.urgency === 'HIGH' || r.urgency === 'CRITICAL')
                  )
                  .slice(0, 6)
                  .map((rec) => (
                    <Card
                      key={`${rec.itemId}-${rec.destinationLocation.id}`}
                      className="border border-border hover:shadow-raised-hover transition-all duration-200"
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <CardTitle className="text-base">{rec.itemName}</CardTitle>
                            <CardDescription className="text-xs font-mono">
                              {rec.itemSku}
                            </CardDescription>
                          </div>
                          <Badge variant="outline" className={cn(urgencyColors[rec.urgency])}>
                            {rec.urgency}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">To:</span>
                          <span className="font-medium">{rec.destinationLocation.name}</span>
                        </div>
                        {rec.suggestedSourceLocation && (
                          <>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">From:</span>
                              <span className="font-medium">
                                {rec.suggestedSourceLocation.name}
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">Distance:</span>
                              <span className="font-medium">
                                {rec.suggestedSourceLocation.distanceMiles != null
                                  ? `${rec.suggestedSourceLocation.distanceMiles.toFixed(0)} mi`
                                  : 'Not known'}
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">Available:</span>
                              <span className="font-medium">
                                {rec.suggestedSourceLocation.availableStock} units
                              </span>
                            </div>
                          </>
                        )}
                        <div className="flex items-center justify-between text-sm pt-2 border-t">
                          <span className="text-muted-foreground">Quantity:</span>
                          <span className="font-bold text-lg">{rec.recommendedQuantity}</span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => handleCreateTransfer(rec)}
                        >
                          <Truck className="h-3 w-3 mr-2" />
                          Create Transfer
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Info Alert */}
      <motion.div variants={fadeUp} className="mt-6">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription className="text-sm text-muted-foreground">
            <strong>Stock Efficiency:</strong> Calculated based on optimal min/max levels vs. actual
            stock. Higher efficiency means less capital tied up in inventory while maintaining
            service levels.
          </AlertDescription>
        </Alert>
      </motion.div>

      {/* Main Table with Tabs */}
      <motion.div variants={fadeUp} className="mt-6">
        <Card className="border-border hover:shadow-raised-hover transition-all duration-200">
          <CardHeader>
            <CardTitle className="text-foreground tracking-tight">
              Replenishment Recommendations
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              {filteredRecommendations.length} of {recommendations.length} recommendations
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList variant="pill" className="grid w-full grid-cols-3 mb-4">
                <TabsTrigger value="all">All ({recommendations.length})</TabsTrigger>
                <TabsTrigger value="critical">Critical ({criticalCount})</TabsTrigger>
                <TabsTrigger value="high">High ({highCount})</TabsTrigger>
              </TabsList>

              <TabsContent value="all">
                <DataTable
                  columns={columns}
                  data={filteredRecommendations}
                  toolbar={urgencySelect}
                  pagination
                  exportable
                  columnToggle
                  emptyTitle={
                    recommendations.length === 0
                      ? 'No replenishment needed'
                      : 'Nothing matches these filters'
                  }
                  emptyDescription={
                    recommendations.length === 0
                      ? 'Every site is at or above its minimum stock level. Recommendations appear here when a site falls below one.'
                      : 'Clear the urgency filter or switch tabs to see the other recommendations.'
                  }
                  emptyAction={
                    recommendations.length === 0 ? (
                      <Button variant="outline" onClick={loadData}>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Re-run analysis
                      </Button>
                    ) : undefined
                  }
                />
              </TabsContent>

              <TabsContent value="critical">
                <DataTable
                  columns={columns}
                  data={filteredRecommendations}
                  toolbar={urgencySelect}
                  pagination
                  exportable
                  columnToggle
                  emptyTitle={
                    recommendations.length === 0
                      ? 'No replenishment needed'
                      : 'Nothing matches these filters'
                  }
                  emptyDescription={
                    recommendations.length === 0
                      ? 'Every site is at or above its minimum stock level. Recommendations appear here when a site falls below one.'
                      : 'Clear the urgency filter or switch tabs to see the other recommendations.'
                  }
                  emptyAction={
                    recommendations.length === 0 ? (
                      <Button variant="outline" onClick={loadData}>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Re-run analysis
                      </Button>
                    ) : undefined
                  }
                />
              </TabsContent>

              <TabsContent value="high">
                <DataTable
                  columns={columns}
                  data={filteredRecommendations}
                  toolbar={urgencySelect}
                  pagination
                  exportable
                  columnToggle
                  emptyTitle={
                    recommendations.length === 0
                      ? 'No replenishment needed'
                      : 'Nothing matches these filters'
                  }
                  emptyDescription={
                    recommendations.length === 0
                      ? 'Every site is at or above its minimum stock level. Recommendations appear here when a site falls below one.'
                      : 'Clear the urgency filter or switch tabs to see the other recommendations.'
                  }
                  emptyAction={
                    recommendations.length === 0 ? (
                      <Button variant="outline" onClick={loadData}>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Re-run analysis
                      </Button>
                    ) : undefined
                  }
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </motion.div>
    </motion.main>
  );
}
